
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { cn } from "@/lib/utils";
import { useAuth } from '@/contexts/auth/useAuth';
import { useCart } from '@/contexts/CartContext';
import { rafThrottle } from '@/utils/throttle';

// Import refactored components
import NavbarContainer from '@/components/navbar/NavbarContainer';
import NavbarLogo from '@/components/navbar/NavbarLogo';
import DesktopNavLinks from '@/components/navbar/DesktopNavLinks';
import NavbarActions from '@/components/navbar/NavbarActions';
import MobileMenu from '@/components/navbar/MobileMenu';
import MobileMenuToggle from '@/components/navbar/MobileMenuToggle';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { getCartCount } = useCart();
  
  // Memoize cart count to prevent unnecessary re-renders
  const cartCount = useMemo(() => getCartCount(), [getCartCount]);
  
  useEffect(() => {
    // Use RAF-based throttle for smoother scroll handling
    const handleScroll = rafThrottle(() => {
      setScrolled(window.scrollY > 10);
    });
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavigation = useCallback((sectionId: string) => {
    if (location.pathname === '/') {
      // Wait for any pending renders, then scroll
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          const navbarHeight = 80; // Account for fixed navbar
          const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - navbarHeight;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    } else {
      navigate('/', { state: { scrollTo: sectionId } });
    }
    setMobileMenuOpen(false);
  }, [location.pathname, navigate]);

  const handleLogoClick = useCallback(() => {
    if (location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  }, [location.pathname, navigate]);

  const toggleMobileMenu = useCallback(() => setMobileMenuOpen(prev => !prev), []);

  return (
    <NavbarContainer scrolled={scrolled}>
      <NavbarLogo onClick={handleLogoClick} />
      
      <DesktopNavLinks onNavigate={handleNavigation} />
      
      <NavbarActions cartCount={cartCount}>
        <MobileMenuToggle 
          isOpen={mobileMenuOpen} 
          onClick={toggleMobileMenu} 
        />
      </NavbarActions>

      {/* Mobile menu */}
      <MobileMenu 
        isOpen={mobileMenuOpen}
        onNavigate={handleNavigation}
      />
    </NavbarContainer>
  );
};

export default React.memo(Navbar);
